// A speed dial: a 240° arc from 0 to `max`, a filled bar for the value that ends where the pointer points, an
// optional amber zone from 0 to `zoneMax` just outside the arc (Q4: below the min injection rate), and an optional
// thin second pointer (Q3's vapour return). Colours are tokens, so the dial flips with the theme.
const SWEEP = 240;
const START = -120;
const R = 80;
const CX = 100;
const CY = 100;
const BAR = 14;

function point(deg: number, r: number) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [CX + r * Math.cos(rad), CY + r * Math.sin(rad)] as const;
}

function arc(fromDeg: number, toDeg: number, r: number) {
  const [x1, y1] = point(fromDeg, r);
  const [x2, y2] = point(toDeg, r);
  return `M ${x1} ${y1} A ${r} ${r} 0 ${toDeg - fromDeg > 180 ? 1 : 0} 1 ${x2} ${y2}`;
}

function angleOf(value: number, max: number) {
  return START + SWEEP * Math.min(1, Math.max(0, max > 0 ? value / max : 0));
}

type Props = {
  value: number | null;
  // A second, thinner pointer on the same scale.
  secondary?: number | null | undefined;
  // An amber zone from 0 to this value.
  zoneMax?: number | null | undefined;
  max: number;
  color: string;
  secondaryColor?: string | undefined;
  label: string;
};

export function Dial({ value, secondary, zoneMax, max, color, secondaryColor, label }: Props) {
  const ticks = [0, 0.25, 0.5, 0.75, 1];
  const valueDeg = value == null ? null : angleOf(value, max);
  return (
    <svg viewBox="0 0 200 172" role="img" aria-label={label} className="w-full max-w-36">
      <path d={arc(START, START + SWEEP, R)} fill="none" stroke="var(--color-secondary-200)" strokeWidth={BAR} />
      {zoneMax != null && zoneMax > 0 && (
        <path d={arc(START, angleOf(zoneMax, max), R + BAR / 2 + 4)} fill="none" stroke="var(--color-amber-500)" strokeWidth={5} />
      )}
      {/* Square ends, so the bar stops exactly at the pointer. */}
      {valueDeg != null && valueDeg > START && (
        <path d={arc(START, valueDeg, R)} fill="none" stroke={color} strokeWidth={BAR} />
      )}
      {ticks.map((t) => {
        const deg = START + SWEEP * t;
        const [x1, y1] = point(deg, R - BAR / 2 - 3);
        const [x2, y2] = point(deg, R - BAR / 2 - 9);
        const [tx, ty] = point(deg, R - BAR / 2 - 22);
        return (
          <g key={t}>
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="var(--color-secondary-400)" strokeWidth={1.5} />
            <text x={tx} y={ty} textAnchor="middle" dominantBaseline="middle" fontSize={15} fill="var(--color-chart-tick)">{Math.round(max * t)}</text>
          </g>
        );
      })}
      {secondary != null && <Needle deg={angleOf(secondary, max)} length={R - BAR / 2} width={2.5} color={secondaryColor ?? color} />}
      {valueDeg != null && <Needle deg={valueDeg} length={R + BAR / 2} width={4} color="var(--color-secondary-900)" />}
      <circle cx={CX} cy={CY} r={7} fill="var(--color-secondary-900)" />
    </svg>
  );
}

function Needle({ deg, length, width, color }: { deg: number; length: number; width: number; color: string }) {
  const [x, y] = point(deg, length);
  return <line x1={CX} y1={CY} x2={x} y2={y} stroke={color} strokeWidth={width} strokeLinecap="round" />;
}
